import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Tabs,
  Tab,
  Box,
  Button,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  ViewList,
  Store as StoreIcon,
  AccountCircle,
  Logout,
  CloudUpload,
  Assignment,
  CalendarMonth,
  AccountBalance,
  Menu as MenuIcon,
  TrendingUp,
  Build,
  Assessment,
  ViewKanban,
  AccountBalanceWallet,
  LocalShipping,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Store } from '../types';

interface LayoutProps {
  children: React.ReactNode;
  selectedStore?: Store | null;
  onChangeStore?: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, selectedStore, onChangeStore }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { dbUser, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Responsive breakpoint hook
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Helper function to check if user has a specific permission
  const hasPermission = (permission: string): boolean => {
    if (!dbUser?.permission) return false;
    const userPermissions = dbUser.permission.split(',').map(p => p.trim());
    return userPermissions.includes(permission);
  };

  // Navigation items configuration
  const navigationItems = React.useMemo(() => [
    { label: 'New Order', icon: <Add />, path: '/' },
    { label: 'View Orders', icon: <ViewList />, path: '/orders' },
    { label: 'Uploader', icon: <CloudUpload />, path: '/uploader' },
    { label: 'Daily Commitment Orders', icon: <Assignment />, path: '/commitment' },
    { label: 'Daily Account', icon: <AccountBalance />, path: '/daily-account' },
    { label: 'Leave Calendar', icon: <CalendarMonth />, path: '/leave-calendar' },
    { label: 'Tasks', icon: <ViewKanban />, path: '/tasks' },
    ...(hasPermission('order_processor') ? [{ label: 'Order Processor', icon: <LocalShipping />, path: '/order-processor' }] : []),
    ...(hasPermission('utilities') ? [{ label: 'Utilities', icon: <Build />, path: '/utilities' }] : []),
    ...(hasPermission('item_reports') ? [{ label: 'Reports', icon: <Assessment />, path: '/reports' }] : []),
    ...(hasPermission('finance_dashboard') ? [{ label: 'Finance Dashboard', icon: <TrendingUp />, path: '/finance-dashboard' }] : []),
    ...(hasPermission('finance_dashboard') ? [{ label: 'Financial Analysis Report', icon: <AccountBalanceWallet />, path: '/financial-analysis-report' }] : []),
  ], [dbUser?.permission]);

  // Find current tab index based on pathname
  const currentTab = React.useMemo(() => {
    // First try exact match for root
    if (location.pathname === '/') {
      return navigationItems.findIndex(item => item.path === '/');
    }
    
    // For other paths, find the best match
    // Sort by path length (descending) to match most specific first
    const sortedItems = navigationItems
      .map((item, index) => ({ ...item, originalIndex: index }))
      .sort((a, b) => b.path.length - a.path.length);
    
    for (const item of sortedItems) {
      if (item.path !== '/' && location.pathname.startsWith(item.path)) {
        return item.originalIndex;
      }
    }
    
    return -1;
  }, [location.pathname, navigationItems]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (newValue >= 0 && newValue < navigationItems.length) {
      const targetPath = navigationItems[newValue].path;
      navigate(targetPath);
    }
    
    // Close mobile menu after navigation
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleUserMenuClose();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <img src="/logo.svg" alt="FirstShield Logo" style={{ width: '32px', height: '32px', marginRight: '16px' }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            FirstShield Order Intake System
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {selectedStore && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  icon={<StoreIcon />}
                  label={selectedStore.store_name}
                  variant="outlined"
                  sx={{ 
                    color: 'white', 
                    borderColor: 'rgba(255, 255, 255, 0.7)',
                    '& .MuiChip-icon': { color: 'white' }
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onChangeStore}
                  sx={{ 
                    color: 'white', 
                    borderColor: 'rgba(255, 255, 255, 0.7)',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  Change Store
                </Button>
              </Box>
            )}
            
            {/* User Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={dbUser?.role || 'user'}
                size="small"
                sx={{ 
                  color: 'white', 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  fontWeight: 'bold'
                }}
              />
              <IconButton
                onClick={handleUserMenuClick}
                sx={{ color: 'white' }}
              >
                {dbUser?.photo_url ? (
                  <Avatar src={dbUser.photo_url} sx={{ width: 32, height: 32 }} />
                ) : (
                  <AccountCircle />
                )}
              </IconButton>
            </Box>
          </Box>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem disabled>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {dbUser?.display_name || 'User'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {dbUser?.email}
                </Typography>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <Logout fontSize="small" sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
        
        {/* Navigation Section */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Container maxWidth="xl">
            {/* Mobile Menu Button */}
            {isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                <IconButton
                  onClick={handleMobileMenuToggle}
                  sx={{ color: 'primary.main' }}
                  aria-label="open navigation menu"
                >
                  <MenuIcon />
                </IconButton>
                <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                  Menu
                </Typography>
              </Box>
            )}
            
            {/* Desktop Navigation Tabs */}
            {!isMobile && (
              <Tabs
                value={currentTab === -1 ? false : currentTab}
                onChange={handleTabChange}
                aria-label="navigation tabs"
                sx={{ minHeight: 48 }}
                variant="scrollable"
                scrollButtons="auto"
              >
                {navigationItems.map((item, index) => (
                  <Tab
                    key={index}
                    icon={item.icon}
                    label={item.label}
                    iconPosition="start"
                    sx={{ minHeight: 48, textTransform: 'none' }}
                  />
                ))}
              </Tabs>
            )}
          </Container>
        </Box>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" color="primary">
            Navigation Menu
          </Typography>
        </Box>
        <List sx={{ pt: 1 }}>
          {navigationItems.map((item, index) => (
            <ListItem
              key={index}
              button
              onClick={() => handleTabChange({} as React.SyntheticEvent, index)}
              selected={currentTab === index}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                  },
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemIcon sx={{ color: currentTab === index ? 'primary.main' : 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: currentTab === index ? 'bold' : 'normal',
                  color: currentTab === index ? 'primary.main' : 'inherit',
                }}
              />
            </ListItem>
          ))}
        </List>
      </Drawer>

      <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
        {children}
      </Container>
    </Box>
  );
};

export default Layout;
